import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import Table from "common/graphs/Table/Table";
import BqnContext from "common/BqnContext";
import Loading from "common/Loading";
import { atBqnTarget, outputsOfBatch } from "common/api";
import { durationToInt } from "common/utils";
import {
  LOAD_INTERVAL,
  RETURN_VIEW,
  PERIOD_DASHBOARD_TO_TIME_PERIOD,
} from "./utils";
import useDashboardData from "./useDashboardData";
import { renderLimitFromMbps, RATE_PRECISIONS } from "views/Status/SubscriberGroups/Report/utils";
import { NumberCell, TextWithIcon } from "common/graphs/Table/TableRenders";
import { parseSubscriberGroupTypeMap } from "views/Status/SubscriberGroups/api";
import { openRatePolicy } from "views/Status/SubscriberGroups/component.jsx";
import { formatMB } from "views/Status/Subscribers/Report/ListSubscribers";

const Wrapper = styled.div`
  width: 100%;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  color: var(--text-primary);
`;

// The types of the groups and their metrics go in a single batch request:
// the CLI runs the commands of the GUI one after the other, so one request
// each would take one turn of its queue each.
const composeSubscriberGroupsBatch = (mode, bqnId) =>
  `${[
    atBqnTarget("show api subscriber-group", mode, bqnId),
    atBqnTarget(
      "show subscriber-group all metrics-downlink lines 10",
      mode,
      bqnId,
    ),
  ].join("\n")}\n`;

const getSubscriberGroups = ({ mode, bqnId }) => {
  return ifCl
    .run(composeSubscriberGroupsBatch(mode, bqnId), "batch_exmode")
    .then((batch) => {
      const [responseTypes, response] = outputsOfBatch(batch);
      const typeMap = parseSubscriberGroupTypeMap(responseTypes, bqnId);

      const [head, ...rows] = response.trim().split("\n");
      if (!head) return [];
      const headerAttrs = head.trim().split(/\s+/);
      const parsedData = rows
        .filter((row) => row.trim() !== "")
        .map((row, id) => {
          const attrs = row.trim().split(/\s+/);
          const item = attrs.reduce(
            (acc, attr, index) => {
              const attrName = headerAttrs[index];
              acc[attrName] = attr;
              return acc;
            },
            { id },
          );
          item["GROUP-TYPE"] = typeMap[item["SUBSCRIBER-GROUP"]];
          return item;
        });
      return parsedData;
    });
};

const getColumns = ({ mode, period, hideRates, bqnId }) => {
  const timePeriod = PERIOD_DASHBOARD_TO_TIME_PERIOD[period];
  return [
    {
      label: "SUBSCRIBER-GROUP",
      id: "SUBSCRIBER-GROUP",
      cellClassName: "hyperlink-text",
      title: "Subscriber’s group",
      numeric: false,
      onClick: (subscriberGroup, row) => {
        globalNavigate("viewStatusSubscriberGroups", {
          subscriberGroup,
          returnView: RETURN_VIEW,
          type: row["GROUP-TYPE"],
          timePeriod,
        });
      },
    },
    {
      label: "SUBS-ACTIVE",
      title: "SUBS-ACTIVE",
      id: "SUBS-ACTIVE",
      numeric: true,
      onClick: (_, row) => {
        globalNavigate("viewStatusSubscriberGroups", {
          subscriberGroup: row["SUBSCRIBER-GROUP"],
          timePeriod,
          view: "qoe",
          type: row["GROUP-TYPE"],
          returnView: RETURN_VIEW,
        });
      },
    },
    {
      label: "FL-ACTIVE",
      id: "FL-ACTIVE",
      title: "FL Active",
      numeric: true,
    },
    {
      label: "FL-CREATED",
      id: "FL-CREATED",
      title: "FL Created",
      numeric: true,
    },
    {
      label: "LIMIT",
      id: "LIM-Mbps",
      title: "LIMIT",
      numeric: true,
      renderCell: (value) => (hideRates ? "n/a" : renderLimitFromMbps(value)),
    },
    ...(hideRates
      ? [
          {
            label: "RATE-POLICY",
            id: "POLICY-RATE",
            title: "RATE-POLICY",
            numeric: false,
            onClick: null,
            cellClassName: null,
            renderCell: (value, row) => {
              const icon = row["ASSIGNMENT"] === undefined ? "warning" : null;
              return <TextWithIcon icon={icon} value={value} />;
            },
          },
        ]
      : [
          {
            label: "RATE-POLICY",
            id: "POLICY-RATE",
            title: "RATE-POLICY",
            numeric: false,
            onClick: (_, row) => {
              const policyName = row["POLICY-RATE"];
              const assignment = row["ASSIGNMENT"];
              if(policyName === "n/a"){
                return;
              }
              openRatePolicy(
                policyName,
                assignment,
                bqnId,
                { direction: "downlink", use: "ipAddress", timePeriod },
                mode,
                RETURN_VIEW,
              );
            },
            renderCell: (value, row) => {
              const icon = row["ASSIGNMENT"] === undefined ? "warning" : null;
              return <TextWithIcon icon={icon} value={value} />;
            },
            cellClassName: (value, row) => {
              return `align-left${value !== "n/a" ? " hyperlink-text" : ""}${
                row["FL-CREATED"] === "undefined" ? " col-red" : ""
              }`;
            },
          },
        ]),
    {
      label: "MBYTES-UP",
      id: "BYTES-UPLINK",
      title: "Total up traffic volume of this subscriber group",
      numeric: true,
      renderCell: (rawValue) => {
        const { value, precision } = formatMB(rawValue);
        return <NumberCell value={value} hasNA={true} precision={precision} />;
      },
    },
    {
      label: "MBYTES-DOWN",
      id: "BYTES-DOWNLINK",
      title: "Total down traffic volume of this subscriber group",
      numeric: true,
      renderCell: (rawValue) => {
        const { value, precision } = formatMB(rawValue);
        return <NumberCell value={value} hasNA={true} precision={precision} />;
      },
    },
    {
      label: "CURR-Mbps",
      id: "CURR-Mbps",
      title: "Current speed in Mbps",
      numeric: true,
      renderCell: (value) => <NumberCell value={value} hasNA={true} />,
    },
    {
      label: "MEAN-Mbps",
      id: "MEAN-Mbps",
      title:
        "Moving average of the mean speed (calculated every 10 minutes) in Mbps.",
      numeric: true,
      renderCell: (value) => (
        <NumberCell precision={RATE_PRECISIONS} value={value} hasNA={true} />
      ),
    },
    {
      id: "RTT-80%-ms",
      label: "RTT-P80-ms",
      title:
        "The 80-th percentile of all RTT latency, measured from BQN to end user, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      id: "RTT-MED-ms",
      label: "RTT-MED-ms",
      title:
        "The 50-th percentile (median) of all RTT latency samples, measured from BQN to end user, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      id: "RTT-MIN-ms",
      label: "RTT-MIN-ms",
      title:
        "The mean of all RTT minimum latency samples, measured in 1-second intervals, during active downloads, in 5 minutes.",
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={1} />
      ),
      numeric: true,
    },
    {
      label: "RTX",
      id: "RTX",
      title:
        "Moving average of the percentage of TCP retransmissions (which reflect packet losses in this direction)",
      numeric: true,
      renderCell: (value) => (
        <NumberCell precision={1} value={value} hasNA={true} percent={true} />
      ),
    },
    {
      label: "CONGESTION",
      id: "CONGEST",
      title: "Moving average of percentage of traffic suffering congestion",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} percent={true} precision={1} />
      ),
    },
    {
      label: "WARN",
      title: "Number of warnings",
      id: "WARN",
      numeric: true,
      renderCell: (value) => (
        <NumberCell value={value} hasNA={true} precision={0} />
      ),
    },
    {
      label: "LIFETIME",
      id: "LIFETIME",
      title: "Duration so far of this subscriber session",
      numeric: true,
      parse: (data) => (data === "n/a" || !data ? 0 : durationToInt(data)),
    },
    {
      label: "GROUP-TYPE",
      title: "Subscriber group type",
      id: "GROUP-TYPE",
      numeric: false,
    },
  ];
};

function SubscriberGroups({ period, profile }) {
  // The columns are those of the BQN selected and of the mode of the
  // cluster, which the table needs and the retrieval takes on its own.
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const { hideRates = false } = profile || {};

  // The table has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data, isLoading } = useDashboardData({
    retrieve: getSubscriberGroups,
    onError: "Error fetching subscriber groups data:",
    interval: LOAD_INTERVAL,
  });

  // Each column carries how to render and to sort its values, so the table
  // is given the same ones while nothing they depend on changes.
  const columns = useMemo(
    () => getColumns({ mode, period, hideRates, bqnId }),
    [mode, period, hideRates, bqnId],
  );

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!data || data.length === 0) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <Table
        isSelectable={false}
        isRowSelectable={false}
        data={data}
        sort={true}
        columns={columns}
        rowsPerPageNr={50}
        exportCSV={false}
        hasColumnFilter={false}
        hasPagination={false}
        hasSelectNrRows={false}
        search={false}
        maxHeight={210}
      />
    </Wrapper>
  );
}

export default React.memo(SubscriberGroups);
